import { useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, Briefcase, Boxes, Building2, MessageSquareQuote, HelpCircle, Settings, LogOut, ExternalLink, Menu, LoaderCircle, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/admin/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/admin/inquiries", label: "Inquiries", Icon: Inbox },
  { group: "Content" },
  { to: "/admin/services", label: "Services", Icon: Briefcase },
  { to: "/admin/products", label: "Products", Icon: Boxes },
  { to: "/admin/industries", label: "Industries", Icon: Building2 },
  { to: "/admin/testimonials", label: "Testimonials", Icon: MessageSquareQuote },
  { to: "/admin/faqs", label: "FAQs", Icon: HelpCircle },
  { group: "System" },
  { to: "/admin/settings", label: "Settings", Icon: Settings },
];

const SidebarNav = () => (
  <nav className="flex flex-col gap-1 p-3" aria-label="Admin">
    {NAV.map((n, i) => n.group ? (
      <p key={`g-${i}`} className="mt-4 px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{n.group}</p>
    ) : (
      <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `admin-nav-item ${isActive ? "active" : ""}`} data-testid={`admin-nav-${n.label.toLowerCase()}`}>
        <n.Icon className="h-4 w-4" /> {n.label}
      </NavLink>
    ))}
  </nav>
);

export const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-background" data-testid="admin-loading"><LoaderCircle className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: location }} />;

  const crumbs = location.pathname.split("/").filter(Boolean).slice(1);

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[260px_1fr]" data-testid="admin-layout">
      <aside className="hidden border-r border-border bg-card lg:flex lg:flex-col">
        <div className="flex h-16 items-center gap-3 border-b border-border px-5"><img src="/assets/logo_badge.png" alt="" className="h-8 w-auto" /><div><p className="font-heading text-sm font-semibold leading-none">JNT Admin</p><p className="mt-1 text-[11px] text-muted-foreground">Content management</p></div></div>
        <div className="flex-1 overflow-y-auto"><SidebarNav /></div>
        <div className="border-t border-border p-3"><a href="/" target="_blank" rel="noreferrer" className="admin-nav-item" data-testid="admin-view-site-link"><ExternalLink className="h-4 w-4" /> View public site</a></div>
      </aside>

      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-border bg-background px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" className="rounded-lg bg-white lg:hidden" onClick={() => setOpen(true)} aria-label="Open admin menu" data-testid="admin-mobile-menu-button"><Menu className="h-4 w-4" /></Button>
            <nav className="flex items-center gap-1 text-sm" aria-label="Breadcrumb"><span className="text-muted-foreground">Admin</span>{crumbs.map((c) => <span key={c} className="flex items-center gap-1"><ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-medium capitalize">{c}</span></span>)}{crumbs.length === 0 && <span className="flex items-center gap-1"><ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-medium">Dashboard</span></span>}</nav>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" className="h-10 rounded-lg bg-white" data-testid="admin-user-menu-button"><span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">{(user.name || user.email)[0].toUpperCase()}</span><span className="hidden text-sm sm:inline">{user.name || user.email}</span></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white">
              <DropdownMenuLabel className="font-normal"><p className="text-sm font-medium">{user.name}</p><p className="text-xs text-muted-foreground">{user.email}</p></DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/admin/settings?tab=security")} data-testid="admin-menu-security"><Settings className="h-4 w-4" /> Account & security</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { logout(); navigate("/admin/login"); }} className="text-destructive focus:text-destructive" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 bg-card p-0">
          <SheetTitle className="sr-only">Admin navigation</SheetTitle>
          <div className="flex h-16 items-center gap-3 border-b border-border px-5"><img src="/assets/logo_badge.png" alt="" className="h-8 w-auto" /><p className="font-heading text-sm font-semibold">JNT Admin</p></div>
          <div onClick={() => setOpen(false)}><SidebarNav /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
